import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, X, Pencil, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useRenameConnector } from '@/hooks/useConnectors';
import { validateConnectorName, CONNECTOR_NAME_ERROR } from '@/lib/validation/connectorName';

interface Props {
  configId: string;
  connectorType: string;
  name: string;
}

export function EditableConnectorName({ configId, connectorType, name }: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const rename = useRenameConnector();

  // Adjust `value` back to the prop `name` whenever `name` OR `editing`
  // changes (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes):
  // the original effect's dependency array was `[name, editing]`, so it reran
  // -- and unconditionally reset `value` when not editing -- on either input
  // changing, not just `name`. Mirroring both in state reproduces that: the
  // guard converges (both mirrors catch up to current props) on the very next
  // render.
  const [prevName, setPrevName] = useState(name);
  const [prevEditing, setPrevEditing] = useState(editing);
  if (name !== prevName || editing !== prevEditing) {
    setPrevName(name);
    setPrevEditing(editing);
    if (!editing) setValue(name);
  }

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const validate = useCallback((v: string): string | null => {
    const t = v.trim();
    if (!t) return 'Name is required';
    if (!validateConnectorName(t)) return CONNECTOR_NAME_ERROR;
    return null;
  }, []);

  const save = useCallback(async () => {
    const trimmed = value.trim();
    const err = validate(trimmed);
    if (err) { setError(err); return; }
    if (trimmed === name) { setEditing(false); setError(null); return; }
    try {
      await rename.mutateAsync({ configId, newName: trimmed });
      navigate(`/connectors/${connectorType}/${encodeURIComponent(trimmed)}${location.search}`);
    } catch (e: unknown) {
      const status = (e as { status?: number })?.status;
      if (status === 409) setError(`A connector named "${trimmed}" already exists`);
      else if (status === 400) setError((e as Error).message || 'Invalid connector name');
      else setError('Failed to rename connector');
    }
  }, [value, name, configId, connectorType, location.search, rename, navigate, validate]);

  const cancel = useCallback(() => { setValue(name); setEditing(false); setError(null); }, [name]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    else if (e.key === 'Escape') { cancel(); }
  }, [save, cancel]);

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <h1 className="truncate text-[1.6rem] font-bold leading-tight tracking-tight">{name}</h1>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => setEditing(true)}
          aria-label="Edit name"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(null); }}
          onKeyDown={onKeyDown}
          maxLength={64}
          className="h-auto px-2 py-1 text-[1.4rem] font-bold"
          disabled={rename.isPending}
          aria-label="Connector name"
        />
        <Button size="icon" variant="ghost" onClick={save} disabled={rename.isPending} aria-label="Save name">
          {rename.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 text-success" />}
        </Button>
        <Button size="icon" variant="ghost" onClick={cancel} disabled={rename.isPending} aria-label="Cancel">
          <X className="h-4 w-4" />
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
